import _isEmpty from 'underscore/cjs/isEmpty.js'
import _isEqual from 'underscore/cjs/isEqual.js'

export const useDashboardStore = defineStore('dashboard', {
  state: () => {
    return {
      top_news: {},
      dashboard_cards: [],
      core_actions: [],
      core_actions_pill_label: null,
      core_actions_pill_theme_token: null,
      cross_sell: null,
      potential_savings: null,
      core_actions_fetched_at: null,
      core_actions_user_id: null,
      cards_revision: 0,
      core_actions_completed_tracked: false
    }
  },

  getters: {
    getTopNews: state => state.top_news,
    getDashboardCards: state => state.dashboard_cards,
    getCoreActions: state => state.core_actions,
    getCoreActionsPillLabel: state => state.core_actions_pill_label,
    getCoreActionsPillThemeToken: state => state.core_actions_pill_theme_token,
    getCrossSell: state => state.cross_sell,
    getPotentialSavings: state => state.potential_savings,
    hasCachedCoreActions: state => state.dashboard_cards.length > 0,
    cardsRevision: state => state.cards_revision,
    getCoreActionsUserId: state => state.core_actions_user_id
  },

  actions: {
    async fetchTopNews ({ limit, offset, categories, preview } = {}) {
      const params = new URLSearchParams()
      if (limit != null) params.append('limit', limit)
      if (offset != null) params.append('offset', offset)
      if (preview != null) params.append('preview', preview)
      if (Array.isArray(categories)) {
        for (const cat of categories) params.append('categories', cat)
      } else if (categories) {
        params.append('categories', categories)
      }
      const topNewsUrl = prependHostIfNeeded('/api/contentful/dashboard/top-news')
      const url = `${topNewsUrl}?${params.toString()}`

      try {
        const responseData = await $fetch(url, { method: 'GET' })
        if (!responseData || _isEmpty(responseData?.top_news)) {
          this.top_news = {}
          return {}
        }
        this.top_news = responseData.top_news || {}
        return responseData
      } catch (err) {
        // Log and fail gracefully
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[dashboardStore] fetchTopNews failed, returning empty set', err?.message || err)
        this.top_news = {}
        return {}
      }
    },
    /**
     * Fetches core actions (and companion data) from the MemberHub API via the Nitro
     * server route `/api/memberhub/user/core-actions`.
     *
     * Response shape consumed:
     *  - `cards[]`                             — ordered normalized dashboard card feed
     *  - `core_actions.core_actions[]`         — compatibility subset of core-action cards
     *  - `potential_savings`                   — savings estimates for the savings tracker
     *
     * Side effects:
     *  - Fires `Core Actions Completed` tracking event once when `completion_percentage`
     *    reaches 100 (deduped via `core_actions_completed_tracked`, re-armed below 100).
     *  - On empty response: all related state is reset to null / [].
     *  - On error: cached cards are kept (stale-while-revalidate) — state is only
     *    wiped when there is no cache to fall back on.
     *  - `cards_revision` increments only when the card payload actually changed,
     *    so the UI can crossfade on real updates and stay still otherwise.
     */
    async fetchCoreActions () {
      const coreActionsUrl = prependHostIfNeeded('/api/memberhub/user/core-actions')
      try {
        const responseData = await $fetch(coreActionsUrl, { method: 'GET' })
        if (!responseData || _isEmpty(responseData?.cards)) {
          const hadCards = this.dashboard_cards.length > 0
          this.dashboard_cards = []
          this.core_actions = []
          this.core_actions_pill_label = null
          this.core_actions_pill_theme_token = null
          this.cross_sell = null
          this.potential_savings = null
          this.core_actions_fetched_at = Date.now()
          this.setCoreActionsUserId()
          if (hadCards) {
            this.cards_revision++
          }
          return { cards: this.dashboard_cards, core_actions: this.core_actions }
        }

        const changed = !_isEqual(this.dashboard_cards, responseData.cards || [])
        this.dashboard_cards = responseData.cards || []
        this.core_actions = responseData.core_actions?.core_actions || []
        this.core_actions_pill_label = responseData.core_actions?.pill_label || null
        this.core_actions_pill_theme_token = responseData.core_actions?.pill_theme_token || null
        this.cross_sell = responseData.cross_sell || null
        this.potential_savings = responseData.potential_savings || null
        this.core_actions_fetched_at = Date.now()
        this.setCoreActionsUserId()
        if (changed) {
          this.cards_revision++
        }

        const { $tracking } = useNuxtApp()

        // Fire additional event when all actions are complete
        if (responseData.core_actions?.completion_percentage >= 100) {
          if (!this.core_actions_completed_tracked) {
            this.core_actions_completed_tracked = true
            $tracking.customEvent('Core Actions Completed', {
              completion_percentage: responseData.core_actions.completion_percentage,
              completed_actions: this.core_actions.filter(action => !!action.completed)
            })
          }
        } else {
          this.core_actions_completed_tracked = false
        }

        return responseData
      } catch (err) {
        if (this.dashboard_cards.length > 0) {
          // eslint-disable-next-line no-console -- diagnostic for ops
          console.warn('[dashboardStore] fetchCoreActions failed, keeping cached cards', err?.message || err)
          return { cards: this.dashboard_cards, core_actions: this.core_actions }
        }
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[dashboardStore] fetchCoreActions failed, returning empty set', err?.message || err)
        this.dashboard_cards = []
        this.core_actions = []
        this.core_actions_pill_label = null
        this.core_actions_pill_theme_token = null
        this.cross_sell = null
        this.potential_savings = null
        return { cards: this.dashboard_cards, core_actions: this.core_actions }
      }
    },
    /**
     * Records which user the cached core-actions payload belongs to, so a
     * different account logging in on the same device is treated as a cold
     * start instead of flashing the previous user's cards.
     */
    setCoreActionsUserId () {
      const hashId = useUserStore().getUserProfile?.hash_id
      if (hashId) {
        this.core_actions_user_id = hashId
      }
    },
    /**
     * Marks the `all_done` card as dismissed using an optimistic update, then persists
     * the dismissal to MemberHub via PATCH `/api/memberhub/user/core-actions`.
     *
     * The local state is updated immediately so the card disappears without waiting for
     * the network round-trip. If the API call fails, the optimistic state is intentionally
     * kept — dismissal is a cosmetic preference, not a critical operation.
     */
    async dismissCoreActionsAllDone () {
      const dismissUrl = prependHostIfNeeded('/api/memberhub/user/core-actions')
      this.dashboard_cards = this.dashboard_cards.map(item => {
        if (item?.actionKey !== 'all_done') {
          return item
        }

        return {
          ...item,
          dismissed: true
        }
      })
      this.core_actions = this.core_actions.map(item => {
        if (item?.action_key !== 'all_done') {
          return item
        }

        return {
          ...item,
          dismissed: true
        }
      })

      try {
        return await $fetch(dismissUrl, { method: 'PATCH' })
      } catch (err) {
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[dashboardStore] dismissCoreActionsAllDone failed, keeping optimistic state', err?.message || err)
        return { data: { dismissed: true } }
      }
    }
  },

  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
    pick: [
      'dashboard_cards',
      'core_actions',
      'core_actions_pill_label',
      'core_actions_pill_theme_token',
      'cross_sell',
      'potential_savings',
      'core_actions_fetched_at',
      'core_actions_user_id',
      'core_actions_completed_tracked'
    ]
  }
})
